/**
 * Resolve the backend base URL.
 *
 * In a same-origin deployment (frontend + backend served from the same host
 * via Kubernetes ingress) we ALWAYS want to use the current page origin so
 * the build is deploy-target-agnostic. This avoids the classic "production
 * bundle baked with preview URL" problem when env vars don't get injected
 * at deploy time.
 *
 * Local development / split-host setups + Capacitor APKs (where the page
 * is served from `https://app` or `https://localhost` and the API lives
 * elsewhere) must ALWAYS use `REACT_APP_BACKEND_URL` verbatim.
 */
export function resolveBackendUrl() {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }

  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  const origin = window.location.origin;
  const hostname = window.location.hostname;

  // Capacitor / local-dev shell hostnames — always trust the env var, since
  // the app is served from a scheme that has nothing to do with the API.
  // We check every known Capacitor pseudo-host so future androidScheme
  // changes don't silently break the APK again.
  const isShellHostname =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === 'app' ||           // Capacitor 7 recommended default
    hostname === 'ionic.local' ||   // legacy iOS
    hostname.endsWith('.local');

  if (isShellHostname) {
    return envUrl || origin;
  }

  // In any deployed environment, prefer the current origin. This makes the bundle
  // portable across preview/production/custom domains regardless of what was
  // baked in at build time.
  try {
    if (envUrl) {
      const envHost = new URL(envUrl).host;
      const currentHost = window.location.host;
      if (envHost === currentHost) return envUrl;
    }
  } catch (_) {
    // Malformed env URL — fall through to origin
  }
  return origin;
}

export const BACKEND_URL = resolveBackendUrl();
